<template>
  <MultiSelect
    v-model="filterProxy"
    :showToggleAll="false"
    :maxSelectedLabels="0"
    :selectedItemsLabel="getSelectedItemsLabel"
    :loading="loading"
    v-bind="$attrs"
    @hide="setFilters"
  />
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es';
import { computed, ref, watch, type ComputedRef, type PropType, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { MultiSelect } from '../../../../controls/multi-select';
const props = defineProps({
  localeValue: {
    type: String,
  },
});

// Init localization
const { t } = useI18n();

// Model value handler
const value = defineModel({ type: [Array] as PropType<string[] | undefined> });

// Proxy value for filter
const filtersToSet: Ref<string[] | null> = ref(null);

// Proxy computed
const filterProxy = computed({
  get(): string[] | null {
    return filtersToSet.value ?? value.value;
  },
  set(val: string[]) {
    filtersToSet.value = val;
    loading.value = true;
    debouncedSetFilters();
  },
});

// Custom label
const getSelectedItemsLabel: ComputedRef<string | undefined> = computed(() => {
  const total = filtersToSet.value?.length ?? value.value?.length;

  if (!props.localeValue) {
    return undefined;
  }
  return t(`tableFilter.selectedItemsLabel`, {
    value: t(props.localeValue, total),
    total,
  });
});

const setFilters = () => {
  loading.value = false;

  if (!filtersToSet.value) {
    return;
  }
  value.value = filtersToSet.value;
};

const loading = ref(false);

const debouncedSetFilters = debounce(() => {
  setFilters();
}, 1000);

watch(value, () => {
  // Reset the filter proxy value when the value changes.
  filtersToSet.value = null;
});
</script>
